import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import api from '../api';
import Candidates from '../pages/Candidates';
import { ResumePreviewModal } from './ResumePreviewFrame';
import { triggerFileDownload, downloadCandidateResumeViaApi } from '../utils/resumePreview';
import CandidateAvatarWithCompletion from './CandidateAvatarWithCompletion';
import CandidatePhoneLink from './CandidatePhoneLink';

import { maskCandidateProfileForClient } from '../utils/maskCandidatePii';

function CandidateProfileReadOnly({ data, onClose, maskPii = false }) {
  const view = maskPii ? maskCandidateProfileForClient(data) : data;
  const skills = Array.isArray(view.skills) ? view.skills : [];
  const education = Array.isArray(view.educational) ? view.educational : [];
  const experience = Array.isArray(view.workExperience) ? view.workExperience : [];
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleResumeDownload = async () => {
    setDownloading(true);
    try {
      const meta = await downloadCandidateResumeViaApi(data.id, {
        candidateName: maskPii ? undefined : view.displayName || data.personal?.name
      });
      triggerFileDownload(meta.blob, meta.fileName);
    } catch (err) {
      window.alert(err.response?.data?.message || err.message || 'Could not download resume');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[14000] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[hsl(var(--ds-surface))] border-b border-[hsl(var(--ds-border))] px-5 py-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-[hsl(var(--ds-fg))] truncate">Candidate profile</h2>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))] shrink-0"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-start gap-4">
            <CandidateAvatarWithCompletion
              candidate={{
                photo: maskPii ? '' : view.photo,
                profileCompletionPercent: view.profileCompletionPercent,
                displayName: view.displayName,
                name: view.displayName
              }}
              size="lg"
              rounded="rounded-full"
            />
            <div className="min-w-0">
              <p className="text-lg font-semibold text-[hsl(var(--ds-fg))]">{view.displayName || '—'}</p>
              <p className="text-sm text-[hsl(var(--ds-muted-fg))]">{view.displayEmail || '—'}</p>
              <CandidatePhoneLink
                phone={view.displayPhone}
                whatsappEnabled={!maskPii}
                className="text-sm"
                showIcon={!maskPii}
              />
              {view.contact?.linkedin ? (
                <p className="text-sm text-[hsl(var(--ds-muted-fg))] break-all">{view.contact.linkedin}</p>
              ) : null}
              <p className="text-sm text-[hsl(var(--ds-muted-fg))] mt-1">
                {view.displayLocation || '—'} · {view.displayExperience || '—'}
              </p>
            </div>
          </div>

          {!maskPii && data.hasResume && data.resumeLink && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="text-sm text-[hsl(var(--ds-primary))] hover:underline"
                onClick={() => setResumePreviewOpen(true)}
              >
                Preview resume
              </button>
              <button
                type="button"
                className="text-sm text-[hsl(var(--ds-fg))] hover:text-[hsl(var(--ds-fg))] font-medium border border-[hsl(var(--ds-border))] px-3 py-1.5 bg-[hsl(var(--ds-surface))] rounded-lg disabled:opacity-50"
                disabled={downloading}
                onClick={handleResumeDownload}
              >
                {downloading ? 'Downloading…' : 'Download'}
              </button>
            </div>
          )}

          {skills.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-2">Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {skills.map(skill => (
                  <span
                    key={skill}
                    className="text-xs px-2 py-0.5 bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border border-[hsl(var(--ds-border))]"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {education.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-2">Education</p>
              <ul className="space-y-2 text-sm text-[hsl(var(--ds-fg))]">
                {education.map((edu, idx) => (
                  <li key={idx}>
                    {[edu.degree, edu.institution, edu.year].filter(Boolean).join(' · ') || '—'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {experience.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide mb-2">
                Work experience
              </p>
              <ul className="space-y-2 text-sm text-[hsl(var(--ds-fg))]">
                {experience.map((exp, idx) => (
                  <li key={idx}>
                    <span className="font-medium">{exp.title || exp.role || 'Role'}</span>
                    {exp.company ? ` at ${exp.company}` : ''}
                    {(exp.from || exp.to) && (
                      <span className="text-[hsl(var(--ds-muted-fg))]">
                        {' '}
                        ({[exp.from, exp.to].filter(Boolean).join(' – ')})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <ResumePreviewModal
        open={resumePreviewOpen}
        onClose={() => setResumePreviewOpen(false)}
        src={data.resumeLink}
        fileName={data.resumeFileName || data.resumeOriginalName}
        candidateName={data.displayName || data.personal?.name}
        candidateId={data.id}
      />
    </div>
  );
}

/**
 * Opens the candidate profile in a popup — full edit for admin/recruiter, read-only for client.
 */
export default function CandidateProfileModal({
  candidateId,
  onClose,
  readOnly = false,
  maskPii = readOnly
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn: () => api.get(`/candidates/${candidateId}`).then(r => r.data),
    enabled: Boolean(candidateId)
  });

  if (!candidateId) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[14000] bg-black/50 flex items-center justify-center p-4">
        <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl px-6 py-4 text-sm text-[hsl(var(--ds-fg))] shadow-xl">
          Loading candidate profile…
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div
        className="fixed inset-0 z-[14000] bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] rounded-xl px-6 py-4 text-sm shadow-xl max-w-sm"
          onClick={e => e.stopPropagation()}
        >
          <p className="text-[hsl(var(--ds-fg))] mb-3">Could not load this candidate profile.</p>
          <button
            type="button"
            className="px-3 py-1.5 text-sm border border-[hsl(var(--ds-border))] hover:bg-[hsl(var(--ds-muted))]"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (readOnly) {
    return <CandidateProfileReadOnly data={data} onClose={onClose} maskPii={maskPii} />;
  }

  return (
    <Candidates
      embedded
      embeddedEditId={candidateId}
      initialCandidate={data}
      onEmbeddedClose={onClose}
    />
  );
}
